// ════════════════════════════════════════════════════════════════════
// Glass-i — Import to Invoice (part deliveries, migration 0186).
//
// A master-detail picker: filter confirmed orders, pick one, then set how many
// pieces of each line this invoice bills. Glass ships in parts — 6 now, 4 next
// week — and each delivery needs its own GST tax invoice.
//
// THE ONLY EDITABLE THING IS "READY".
//   Every other number is read from the document: Ordered is what was sold,
//   Ready is what production has finished, Dispatched is what physically left,
//   Invoiced is what has already been billed, Pending is what remains. Ready Qty
//   is the operator's decision, seeded with min(ready, pending) so the common
//   case — "bill what is made" — needs no typing.
//
// THE CLIENT DOES NOT ENFORCE THE LIMIT.
//   The input caps at Pending as a courtesy, but the real guard is server-side
//   and transactional (Σ invoiced ≤ ordered, per line, under a row lock). Two
//   operators invoicing one order at the same moment cannot both pass it. So a
//   409 here is shown as a clear message, not treated as impossible.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Modal, Table, Input, Button, Alert, Tag, Space, DatePicker, InputNumber,
  Typography, Empty, Tooltip, message
} from 'antd';
import { SearchOutlined, ReloadOutlined, ImportOutlined } from '@ant-design/icons';
import { invoiceApi } from './invoiceApi';
import { mmToFractionalInches } from './invoiceHelpers';

const { Text } = Typography;

function errCode(err) {
  return err && err.response && err.response.data && err.response.data.error
    && err.response.data.error.code;
}
function errMessage(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error
    && err.response.data.error.message) || (err && err.message) || fallback;
}

// The three conflicts the operator can actually act on. Anything else falls
// through to the server's own wording rather than being flattened into
// "something went wrong", which tells them nothing.
function conflictMessage(code, raw) {
  if (code === 'PROFORMA_NOT_CONFIRMED') {
    return 'This order is not confirmed yet. Only a CONFIRMED proforma can be invoiced — confirm it first.';
  }
  if (code === 'INVOICE_QTY_EXCEEDS_ORDERED') {
    return raw; // the server names the line and what is actually left
  }
  if (code === 'INVOICE_NO_LINES') {
    return 'Nothing selected. Enter a Ready Qty of 1 or more on at least one line.';
  }
  return raw;
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// The API returns ISO dates; every date the operator sees in this app is
// dd-mm-yyyy. Left as ISO the column also wrapped onto two lines, which made
// the grid read as double-height rows.
const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return (y && m && d) ? `${d}-${m}-${y}` : String(iso);
};

export default function ImportProformaModal({ open, onClose, onImported }) {
  // ── filters ──
  const [proformaNo, setProformaNo] = useState('');
  const [workOrderNo, setWorkOrderNo] = useState('');
  const [customer, setCustomer] = useState('');
  const [range, setRange] = useState(null);

  // ── data ──
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [linesLoading, setLinesLoading] = useState(false);
  const [ready, setReady] = useState({});          // proformaLineId -> qty
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState(null);
  const reqRef = useRef(0);

  const loadCandidates = useCallback(async () => {
    setLoading(true); setNotice(null);
    const seq = ++reqRef.current;
    try {
      const params = { limit: 200 };
      if (proformaNo.trim()) params.proformaNo = proformaNo.trim();
      if (workOrderNo.trim()) params.workOrderNo = workOrderNo.trim();
      if (customer.trim()) params.customer = customer.trim();
      if (range && range[0]) params.dateFrom = range[0].format('YYYY-MM-DD');
      if (range && range[1]) params.dateTo = range[1].format('YYYY-MM-DD');
      const data = await invoiceApi.importCandidates(params);
      // Ignore a response that a newer search has already superseded, or the
      // grid flickers back to stale rows when two searches overlap.
      if (seq !== reqRef.current) return;
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (seq !== reqRef.current) return;
      setRows([]);
      setNotice({ type: 'error', text: errMessage(e, 'Could not load pending orders.') });
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [proformaNo, workOrderNo, customer, range]);

  useEffect(() => {
    if (!open) return;
    setSelected(null); setDetail(null); setReady({}); setNotice(null);
    loadCandidates();
    // Deliberately only on open: typing in a filter should not re-query on every
    // keystroke — the operator presses Search (or Enter).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickOrder = useCallback(async (row) => {
    setSelected(row); setDetail(null); setReady({}); setNotice(null);
    setLinesLoading(true);
    try {
      const data = await invoiceApi.importLines(row.proformaId);
      setDetail(data);
      // Seed Ready with what is both MADE and still BILLABLE. Ready beyond
      // pending would be refused; pending beyond ready is not yet produced.
      const seed = {};
      for (const l of (data.lines || [])) {
        seed[l.proformaLineId] = Math.max(0, Math.min(num(l.readyQty), num(l.pendingQty)));
      }
      setReady(seed);
    } catch (e) {
      setNotice({ type: 'error', text: errMessage(e, 'Could not load the order lines.') });
    } finally { setLinesLoading(false); }
  }, []);

  const setLineReady = (id, v) => setReady((p) => ({ ...p, [id]: Math.max(0, num(v)) }));

  const fillAll = (pick) => {
    if (!detail) return;
    const next = {};
    for (const l of detail.lines) next[l.proformaLineId] = Math.max(0, pick(l));
    setReady(next);
  };

  const totalReady = useMemo(
    () => Object.values(ready).reduce((a, b) => a + num(b), 0), [ready]);

  const doImport = async () => {
    if (!selected) return;
    const lines = Object.entries(ready)
      .map(([proformaLineId, qty]) => ({ proformaLineId, qty: num(qty) }))
      .filter((l) => l.qty > 0);
    if (!lines.length) {
      setNotice({ type: 'warning', text: 'Enter a Ready Qty of 1 or more on at least one line.' });
      return;
    }
    setImporting(true); setNotice(null);
    try {
      const created = await invoiceApi.importFromProforma(selected.proformaId, { lines });
      message.success(`Draft invoice created for ${totalReady} piece${totalReady === 1 ? '' : 's'}.`);
      if (onImported) onImported(created);
    } catch (e) {
      setNotice({ type: 'error', text: conflictMessage(errCode(e), errMessage(e, 'Import failed.')) });
    } finally { setImporting(false); }
  };

  // ── parent grid ──
  const orderCols = [
    { title: 'PI No', dataIndex: 'proformaNo', width: 170, ellipsis: true,
      render: (v) => <Text strong style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'PI Date', dataIndex: 'proformaDate', width: 115, render: fmtDate },
    { title: 'WO No', dataIndex: 'workOrderNo', width: 160, ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'WO Date', dataIndex: 'workOrderDate', width: 115,
      render: (v) => (v ? fmtDate(v) : <Text type="secondary">—</Text>) },
    { title: 'Customer', dataIndex: 'customerName', ellipsis: true },
    { title: 'Total Qty', dataIndex: 'orderedQty', width: 90, align: 'right' },
    { title: 'Billed Qty', dataIndex: 'invoicedQty', width: 95, align: 'right',
      render: (v) => (num(v) > 0 ? <Text type="warning">{v}</Text> : v) },
    { title: 'Pending Qty', dataIndex: 'pendingQty', width: 100, align: 'right',
      render: (v) => <Text strong style={{ color: num(v) > 0 ? '#16A34A' : undefined }}>{v}</Text> }
  ];

  // ── child grid ──
  const lineCols = [
    { title: 'S.No', width: 50, align: 'right', render: (_v, _r, i) => i + 1 },
    { title: 'Type', dataIndex: 'lineType', width: 95, ellipsis: true,
      render: (v) => (v ? <Tag style={{ marginInlineEnd: 0 }}>{v}</Tag> : null) },
    { title: 'Item Description', dataIndex: 'itemDesc', ellipsis: true, width: 220 },
    { title: 'W (mm)', dataIndex: 'widthMm', width: 80, align: 'right',
      render: (v) => (v ? Number(v).toFixed(2) : '') },
    { title: 'H (mm)', dataIndex: 'heightMm', width: 80, align: 'right',
      render: (v) => (v ? Number(v).toFixed(2) : '') },
    { title: 'W (in)', dataIndex: 'widthMm', key: 'win', width: 75, align: 'right',
      render: (v) => mmToFractionalInches(v) },
    { title: 'H (in)', dataIndex: 'heightMm', key: 'hin', width: 75, align: 'right',
      render: (v) => mmToFractionalInches(v) },
    { title: 'Qty', dataIndex: 'orderedQty', width: 62, align: 'right' },
    { title: 'Remarks', dataIndex: 'remarks', width: 110, ellipsis: true },
    { title: 'Ready', dataIndex: 'readyQty', width: 70, align: 'right',
      render: (v) => (
        <Tooltip title="Pieces production has finished — the same definition the dispatch board uses.">
          <span>{v}</span>
        </Tooltip>
      ) },
    { title: 'Invoiced', dataIndex: 'invoicedQty', width: 78, align: 'right',
      render: (v) => (num(v) > 0 ? <Text type="warning">{v}</Text> : v) },
    { title: 'Pending', dataIndex: 'pendingQty', width: 78, align: 'right',
      render: (v) => <Text strong>{v}</Text> },
    {
      title: 'Ready Qty', key: 'input', width: 104, align: 'right', fixed: 'right',
      render: (_v, r) => (
        <InputNumber
          size="small" min={0} max={num(r.pendingQty)} step={1} precision={0}
          value={ready[r.proformaLineId]}
          onChange={(v) => setLineReady(r.proformaLineId, v)}
          style={{ width: '100%', background: '#FEFCE8' }}
        />
      )
    }
  ];

  const summary = detail && detail.summary;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Import to Invoice"
      width="95vw"
      style={{ top: 16, maxWidth: 1800 }}
      styles={{ body: { paddingTop: 12 } }}
      destroyOnHidden
      footer={[
        <Space key="f" style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>
            {selected
              ? <Text type="secondary">
                  {selected.proformaNo} · {totalReady} piece{totalReady === 1 ? '' : 's'} selected
                </Text>
              : <Text type="secondary">Select an order to load its lines</Text>}
          </span>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" icon={<ImportOutlined />} loading={importing}
              disabled={!selected || totalReady <= 0} onClick={doImport}>
              Import to Invoice
            </Button>
          </Space>
        </Space>
      ]}
    >
      {/* ── filters ── */}
      <Space wrap style={{ marginBottom: 10 }}>
        <Input size="small" allowClear placeholder="PI No" style={{ width: 150 }}
          value={proformaNo} onChange={(e) => setProformaNo(e.target.value)}
          onPressEnter={loadCandidates} prefix={<SearchOutlined />} />
        <Input size="small" allowClear placeholder="WO No" style={{ width: 150 }}
          value={workOrderNo} onChange={(e) => setWorkOrderNo(e.target.value)}
          onPressEnter={loadCandidates} prefix={<SearchOutlined />} />
        <Input size="small" allowClear placeholder="Customer" style={{ width: 200 }}
          value={customer} onChange={(e) => setCustomer(e.target.value)}
          onPressEnter={loadCandidates} prefix={<SearchOutlined />} />
        <DatePicker.RangePicker size="small" value={range} onChange={setRange} format="DD-MM-YYYY" />
        <Button size="small" type="primary" icon={<SearchOutlined />} onClick={loadCandidates}>Search</Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadCandidates} />
      </Space>

      {notice ? (
        <Alert style={{ marginBottom: 10 }} type={notice.type} showIcon
          message={notice.text} closable onClose={() => setNotice(null)} />
      ) : null}

      {/* ── parent: confirmed orders with something left to bill ── */}
      <Table
        size="small" rowKey="proformaId" columns={orderCols} dataSource={rows}
        loading={loading} scroll={{ y: 260, x: 1050 }} pagination={false}
        locale={{ emptyText: <Empty description="No confirmed orders with a pending quantity" /> }}
        rowClassName={(r) => (selected && r.proformaId === selected.proformaId ? 'ant-table-row-selected' : '')}
        onRow={(r) => ({ onClick: () => pickOrder(r), style: { cursor: 'pointer' } })}
      />

      {/* ── child: the lines of the picked order ── */}
      <div style={{ marginTop: 14 }}>
        <Space style={{ marginBottom: 8 }} wrap>
          <Text strong>Order lines</Text>
          {summary ? (
            <Text type="secondary">
              ordered {summary.orderedQty} · ready {summary.readyQty} · invoiced {summary.invoicedQty}
              {' '}· pending {summary.pendingQty}
            </Text>
          ) : null}
          {detail ? (
            <>
              <Button size="small" onClick={() => fillAll((l) => Math.min(num(l.readyQty), num(l.pendingQty)))}>
                Fill with Ready
              </Button>
              <Button size="small" onClick={() => fillAll((l) => num(l.pendingQty))}>
                Fill with Pending
              </Button>
              <Button size="small" onClick={() => fillAll(() => 0)}>Clear</Button>
            </>
          ) : null}
        </Space>

        <Table
          size="small" rowKey="proformaLineId" columns={lineCols}
          dataSource={(detail && detail.lines) || []}
          loading={linesLoading} pagination={false} scroll={{ y: 300, x: 1180 }}
          locale={{
            emptyText: (
              <Empty description={selected
                ? 'This order has no billable lines left'
                : 'Select an order above'} />
            )
          }}
        />
      </div>
    </Modal>
  );
}
